import Phaser from 'phaser';
import { AudioManager } from '../Systems/AudioManager';

export class PlayerController extends Phaser.Physics.Arcade.Sprite {
  // Movement Constants
  private readonly WALK_SPEED = 220;
  private readonly RUN_SPEED = 340;
  private readonly JUMP_FORCE = -460;
  private readonly WALL_SLIDE_SPEED = 80;
  private readonly WALL_JUMP_FORCE_X = 280;
  private readonly WALL_JUMP_FORCE_Y = -420;

  // Jump State & Timing Polish
  private canDoubleJump = false;
  private isWallSliding = false;
  private wallDirection = 0; // -1 left, 1 right

  private coyoteTimer = 0;
  private readonly COYOTE_TIME_MAX = 130; // ms

  private jumpBufferTimer = 0;
  private readonly JUMP_BUFFER_MAX = 130; // ms

  // Climbing State
  public isClimbing = false;
  public touchingVineThisFrame = false;

  // Combat & Health State
  public hp = 3;
  public maxHp = 3;
  public isInvulnerable = false;
  private invulnerableTimer: Phaser.Time.TimerEvent | null = null;

  // Controls
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private keyA!: Phaser.Input.Keyboard.Key;
  private keyD!: Phaser.Input.Keyboard.Key;
  private keyW!: Phaser.Input.Keyboard.Key;
  private keyS!: Phaser.Input.Keyboard.Key;
  private keyShift!: Phaser.Input.Keyboard.Key;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, 'player');

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.setCollideWorldBounds(true);
    this.setBounce(0, 0);
    this.setDragX(1200);

    // Setup physics body size
    if (this.body) {
      this.body.setSize(24, 28);
      this.body.setOffset(4, 4);
    }

    this.setupInput();
  }

  private setupInput(): void {
    if (!this.scene.input.keyboard) return;

    this.cursors = this.scene.input.keyboard.createCursorKeys();
    this.keyA = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyD = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    this.keyW = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W);
    this.keyS = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S);
    this.keyShift = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);
  }

  public updatePlayer(delta: number): void {
    if (!this.body || this.hp <= 0) return;

    const body = this.body as Phaser.Physics.Arcade.Body;
    const isGrounded = body.blocked.down || body.touching.down;

    // Coyote Time update
    if (isGrounded) {
      this.coyoteTimer = this.COYOTE_TIME_MAX;
      this.canDoubleJump = true;
    } else if (this.coyoteTimer > 0) {
      this.coyoteTimer -= delta;
    }

    // Jump Buffer update
    const jumpRequested =
      Phaser.Input.Keyboard.JustDown(this.cursors.space) ||
      Phaser.Input.Keyboard.JustDown(this.cursors.up) ||
      Phaser.Input.Keyboard.JustDown(this.keyW);

    if (jumpRequested) {
      this.jumpBufferTimer = this.JUMP_BUFFER_MAX;
    } else if (this.jumpBufferTimer > 0) {
      this.jumpBufferTimer -= delta;
    }

    // Check Wall Slide
    const isTouchingLeft = body.blocked.left || body.touching.left;
    const isTouchingRight = body.blocked.right || body.touching.right;
    this.isWallSliding = !isGrounded && (isTouchingLeft || isTouchingRight) && body.velocity.y > 0;

    if (this.isWallSliding) {
      this.wallDirection = isTouchingLeft ? -1 : 1;
      body.setVelocityY(Math.min(body.velocity.y, this.WALL_SLIDE_SPEED));
    }

    // Movement direction
    let moveDir = 0;
    if (this.cursors.left.isDown || this.keyA.isDown) moveDir -= 1;
    if (this.cursors.right.isDown || this.keyD.isDown) moveDir += 1;

    // Vine Climbing Logic & Dismount Check
    if (this.isClimbing) {
      if (!this.touchingVineThisFrame) {
        this.isClimbing = false;
        body.setAllowGravity(true);
      } else {
        body.setAllowGravity(false);

        // Allow horizontal dismount off vine
        if (moveDir !== 0 && !this.cursors.up.isDown && !this.keyW.isDown) {
          this.isClimbing = false;
          body.setAllowGravity(true);
        } else {
          let climbVelY = 0;
          if (this.cursors.up.isDown || this.keyW.isDown) climbVelY = -180;
          else if (this.cursors.down.isDown || this.keyS.isDown) climbVelY = 180;
          body.setVelocityY(climbVelY);

          if (this.jumpBufferTimer > 0) {
            this.isClimbing = false;
            body.setAllowGravity(true);
            this.executeJump();
          }
          this.touchingVineThisFrame = false;
          return;
        }
      }
    } else {
      body.setAllowGravity(true);
    }
    this.touchingVineThisFrame = false;

    const isRunning = this.keyShift.isDown;
    const speed = isRunning ? this.RUN_SPEED : this.WALK_SPEED;

    if (moveDir !== 0) {
      body.setVelocityX(moveDir * speed);
      this.setFlipX(moveDir < 0);
    }

    // Jump Execution (Ground Jump / Coyote Jump / Wall Jump / Double Jump)
    if (this.jumpBufferTimer > 0) {
      if (this.coyoteTimer > 0) {
        // Standard / Coyote Jump
        this.executeJump();
      } else if (this.isWallSliding) {
        // Wall Jump
        this.executeWallJump();
      } else if (this.canDoubleJump) {
        // Double Jump
        this.executeDoubleJump();
      }
    }
  }

  private executeJump(): void {
    if (!this.body) return;
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocityY(this.JUMP_FORCE);
    this.coyoteTimer = 0;
    this.jumpBufferTimer = 0;
    AudioManager.playSFX('jump');
  }

  private executeWallJump(): void {
    if (!this.body) return;
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocityX(-this.wallDirection * this.WALL_JUMP_FORCE_X);
    body.setVelocityY(this.WALL_JUMP_FORCE_Y);
    this.isWallSliding = false;
    this.canDoubleJump = true;
    this.jumpBufferTimer = 0;
    AudioManager.playSFX('jump');
  }

  private executeDoubleJump(): void {
    if (!this.body) return;
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocityY(this.JUMP_FORCE * 0.9);
    this.canDoubleJump = false;
    this.jumpBufferTimer = 0;
    AudioManager.playSFX('jump');
  }

  public takeDamage(amount: number = 1): void {
    if (this.isInvulnerable || this.hp <= 0) return;

    this.hp -= amount;
    this.isInvulnerable = true;
    AudioManager.playSFX('enemy_hit');

    // Knockback
    if (this.body) {
      const body = this.body as Phaser.Physics.Arcade.Body;
      body.setVelocityY(-250);
      body.setVelocityX(this.flipX ? 200 : -200);
    }

    // Flashing Invincibility Visual
    const tween = this.scene.tweens.add({
      targets: this,
      alpha: 0.3,
      duration: 100,
      yoyo: true,
      repeat: 8,
      onComplete: () => {
        this.alpha = 1.0;
        this.isInvulnerable = false;
      },
    });
  }

  public bounceUpward(force: number = -380): void {
    if (this.body) {
      const body = this.body as Phaser.Physics.Arcade.Body;
      body.setVelocityY(force);
      this.canDoubleJump = true;
    }
  }

  public heal(amount: number = 1): void {
    this.hp = Math.min(this.maxHp, this.hp + amount);
  }
}
